import { useEffect, useState, useCallback } from 'react'
import { useAxios } from '@/context/axiosContext'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  StatusType,
  GolfArea,
  GolfInfoData,
  KeywordType,
  PromotionData,
  PromoType,
  GolfBizNm,
  formatDate,
  GetPromotionData,
} from '../promotionItem'
import Pagination from '@/pages/board/_components/pagination'
import { Badge, Button, Select } from '@/components'
import GGOOLLFF from '@/assets/images/GGOOLLFF.png'
import commonUtils from '@/utils/commonUtils'
import { useDialog } from '@/context/dialogContext'
import './promotion.css'
import { useResponsive } from '@/hooks/useResponsive'

export const PromotionList = () => {
  const axios = useAxios()
  const location = useLocation()
  const navigate = useNavigate()
  const { showAlert } = useDialog()
  const filePath = process.env.REACT_APP_BASE_URL + '/upload'

  const [page, setPage] = useState(location.state?.page ?? 1)
  const [keyword, setKeyword] = useState('')
  const [checkboxValues, setCheckboxValues] = useState<{
    promoType: string
    statusType: string
    keywordTypes: string[]
  }>({
    promoType: '00',
    statusType: '00',
    keywordTypes: ['nttSj'],
  })

  const [promoList, setPromoList] = useState<PromotionData[]>([])
  const [golfInfoList, setGolfInfoList] = useState<GolfInfoData[]>([])
  const [areaList, setAreaList] = useState<GolfArea[]>([])
  const [nameList, setNameList] = useState<GolfBizNm[]>([])
  const [fileSeqList, setFileSeqList] = useState<string[]>([])
  const [pageCount, setPageCount] = useState(1)
  const [areaCd, setAreaCd] = useState<string>('')
  const { isMobile, isTablet, isDesktop } = useResponsive()

  useEffect(() => {
    window.scrollTo(0, 0) // behavior: 'smooth' 없이 즉시 이동
  }, [])

  // API 호출 함수를 useCallback
  const getPromotionList = useCallback(
    async (targetPage: number = page) => {
      try {
        const params: GetPromotionData = {
          promoType: checkboxValues.promoType,
          statusType: checkboxValues.statusType,
          page: targetPage, // 명시적으로 페이지 전달
        }

        if (checkboxValues.promoType)
          params.promoType = checkboxValues.promoType
        if (checkboxValues.statusType)
          params.statusType = checkboxValues.statusType
        if (checkboxValues.keywordTypes.length > 0)
          params.keywordTypes = checkboxValues.keywordTypes.join(',')
        if (keyword) params.keyword = keyword
        if (checkboxValues.promoType === '01' && areaCd) {
          params.areaCd = areaCd
        }

        const response = await axios.get(`/promotion/list`, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { ...params },
        })

        const { promoList, golfInfoList, pageCnt } = response.data.data

        if (Array.isArray(promoList)) {
          setPromoList(promoList)
          setGolfInfoList(golfInfoList)
          setFileSeqList(fileSeqList)
        }
        setPageCount(pageCnt)

        const areaList: GolfArea[] = Array.from(
          new Map<string, GolfArea>(
            golfInfoList
              .filter(
                (item: GolfInfoData) =>
                  item.areaCd?.trim() && item.remark?.trim(),
              )
              .map((item: GolfInfoData) => [
                item.areaCd!.trim(),
                {
                  areaCd: item.areaCd!.trim(),
                  remark: item.remark!.trim(),
                },
              ]),
          ).values(),
        ).sort((a, b) => a.areaCd.localeCompare(b.areaCd))

        areaList.unshift({
          areaCd: '00',
          remark: '전국',
        })

        const seenBizCds = new Set<string>()
        const nameList: GolfBizNm[] = golfInfoList
          .filter((item: GolfInfoData) => {
            const isValid = item.remark?.trim()
            const isUnique = !seenBizCds.has(item.bizCd || '')

            if (isValid && isUnique) {
              seenBizCds.add(item.bizCd || '')
              return true
            }
            return false
          })
          .map((item: GolfInfoData) => ({
            areaCd: item.areaCd?.trim(),
            bizNm: item.bizNm?.trim(),
            bizCd: item.bizCd,
          }))

        setNameList(nameList)
        setAreaList(areaList)
      } catch (err: any) {
        console.error('Error fetching List:', err)
      }
    },
    [axios, checkboxValues, keyword, areaCd, page, fileSeqList],
  )

  // 초기 로드 및 페이지 변경 시
  useEffect(() => {
    getPromotionList(page)
  }, [page])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value)
  }

  const handleCheckboxChange = (
    group: keyof typeof checkboxValues,
    value: string,
  ) => {
    setCheckboxValues((prev) => {
      if (group === 'promoType' || group === 'statusType') {
        return {
          ...prev,
          [group]: value,
        }
      } else {
        const isChecked = (prev[group] as string[]).includes(value)
        return {
          ...prev,
          [group]: isChecked
            ? (prev[group] as string[]).filter((v) => v !== value)
            : [...(prev[group] as string[]), value],
        }
      }
    })
  }

  // const handleSearchClick = () => {
  //   if (checkboxValues.keywordTypes.length === 0) {
  //     showAlert({
  //       title: '검색 조건을 하나 이상 선택해주세요.',
  //     })
  //     return
  //   }

  //   // 검색 시 페이지를 1로 리셋하고 API 호출
  //   if (page === 1) {
  //     getPromotionList(1) // 이미 1페이지면 직접 호출
  //   } else {
  //     setPage(1) // 페이지 변경으로 useEffect 트리거
  //   }
  // }

  const handleSearchClick = () => {
    const hasCheckedKeywordType = checkboxValues.keywordTypes.length > 0
    const hasSearchKeyword = keyword.trim().length > 0 // keyword는 검색창 값

    if (!hasCheckedKeywordType && !hasSearchKeyword) {
      showAlert({
        title: '검색 조건을 하나 이상 선택해주세요.',
      })
      return
    }

    // 검색 시 페이지를 1로 리셋하고 API 호출
    if (page === 1) {
      getPromotionList(1) // 이미 1페이지면 직접 호출
    } else {
      setPage(1) // 페이지 변경으로 useEffect 트리거
    }
  }

  // const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === 'Enter') {
  //     if (checkboxValues.keywordTypes.length === 0) {
  //       showAlert({
  //         title: '검색 조건을 하나 이상 선택해주세요.',
  //       })
  //     } else {
  //       handleSearchClick()
  //     }
  //   }
  // }

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const hasKeywordTypes = checkboxValues.keywordTypes.length > 0
      const hasKeywordInput = keyword.trim().length > 0

      if (!hasKeywordTypes && !hasKeywordInput) {
        showAlert({
          title: '검색 조건을 하나 이상 선택해주세요.',
        })
        return
      }
      handleSearchClick()
    }
  }

  // 추가: 서버에서 받은 데이터를 로그로 확인
  // useEffect(() => {
  //   if (promoList.length > 0) {
  //     console.log('=== 프로모션 리스트 데이터 디버깅 ===')
  //     promoList.forEach((promo, index) => {
  //       if (!promo.thumbnailUrl || promo.thumbnailUrl === 'null') {
  //         console.warn(
  //           `인덱스 ${index}번 프로모션의 thumbnailUrl이 비어있습니다:`,
  //           promo,
  //         )
  //       }
  //     })
  //     console.log('============================')
  //   }
  // }, [promoList])

  // PromotionList 컴포넌트 상단에 함수 추가
  const getThumbnailUrl = (thumbnailUrl: string | null | undefined) => {
    // null, undefined, 'null', 'undefined' 문자열 체크
    if (
      !thumbnailUrl ||
      thumbnailUrl === 'null' ||
      thumbnailUrl === 'undefined' ||
      thumbnailUrl.trim() === ''
    ) {
      return GGOOLLFF
    }
    return filePath + thumbnailUrl
  }

  return (
    <>
      {!isMobile ? (
        <>
          <div className="list-container">
            <div className="list-select-group">
              <div className="search-criteria">
                <label>프로모션 종류&nbsp;&nbsp;:</label>
                <div className="radio-group">
                  {PromoType.map((type) => (
                    <label key={type.value}>
                      <input
                        type="radio"
                        name="promoType"
                        value={type.value}
                        checked={checkboxValues.promoType === type.value}
                        onChange={() =>
                          handleCheckboxChange('promoType', type.value)
                        }
                      />
                      {type.label}
                      {type.value === '01' &&
                        checkboxValues.promoType === '01' && (
                          <Select
                            id="areaCd"
                            name="areaCd"
                            width="100px"
                            style={{ marginLeft: '8px' }}
                            onChange={(e) => setAreaCd(e.target.value)}
                          >
                            {areaList.map((option, index) => (
                              <option
                                key={`${option.areaCd}-${index}`}
                                value={option.areaCd}
                              >
                                {option.remark}
                              </option>
                            ))}
                          </Select>
                        )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="list-group">
              <div className="search-criteria">
                <label>진행상태&nbsp;&nbsp;:</label>
                <div className="radio-group">
                  {StatusType.map((type) => (
                    <label key={type.value}>
                      <input
                        type="radio"
                        name="statusType"
                        value={type.value}
                        checked={checkboxValues.statusType === type.value}
                        onChange={() =>
                          handleCheckboxChange('statusType', type.value)
                        }
                      />
                      {type.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="list-group search-row">
              <div className="search-criteria">
                <label>검색조건&nbsp;&nbsp;:</label>
                <div className="radio-group">
                  {KeywordType.map((type) => (
                    <label key={type.value}>
                      <input
                        type="checkbox"
                        name="keywordType"
                        value={type.value}
                        checked={checkboxValues.keywordTypes.includes(
                          type.value,
                        )}
                        onChange={() =>
                          handleCheckboxChange('keywordTypes', type.value)
                        }
                      />
                      {type.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="search-box">
                <input
                  type="text"
                  placeholder="검색어 입력"
                  style={{ width: '300px' }}
                  value={keyword}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyPress}
                />
                <Button color="neverbe" onClick={handleSearchClick}>
                  검색
                </Button>
              </div>
            </div>
          </div>
          {promoList.length > 0 ? (
            <>
              <div className="promo-container">
                {promoList.map((promo) => {
                  const endDateUnlimited =
                    promo.prmtEndDate?.startsWith('2099-12-31')
                  const isExpired =
                    promo.prmtEndDate &&
                    new Date(promo.prmtEndDate) < new Date()
                  const now = new Date()
                  const startDate = promo.prmtStartDate
                    ? new Date(promo.prmtStartDate)
                    : null
                  const endDate = promo.prmtEndDate
                    ? new Date(promo.prmtEndDate)
                    : null

                  let status = '진행중'
                  if (startDate && now < startDate) status = '예정'
                  else if (endDate && now > endDate) status = '마감'

                  return (
                    <div
                      className="promo-item"
                      key={promo.nttSn}
                      onClick={() => {
                        navigate(`/promotion/detail?nttSn=${promo.nttSn}`)
                      }}
                      style={{
                        filter: isExpired ? 'grayscale(100%)' : 'none',
                      }}
                    >
                      <img
                        className="img"
                        src={getThumbnailUrl(promo.thumbnailUrl)}
                        title={promo.nttSj}
                        alt="thumbnail"
                        onError={(e) => {
                          e.currentTarget.onerror = null
                          e.currentTarget.src = GGOOLLFF
                        }}
                      />
                      <div className="promo-text">
                        <div className="promo-text-flex">
                          <Badge
                            color={
                              status === '예정'
                                ? 'neverbe'
                                : status === '마감'
                                  ? 'info'
                                  : 'success'
                            }
                          >
                            {status}
                          </Badge>
                          <div className="promo-nttsj" title={promo.nttSj}>
                            {promo.nttSj}
                          </div>
                        </div>
                        기간 :&nbsp;
                        {endDateUnlimited
                          ? `${formatDate(promo?.prmtStartDate || '', true)} ~ 종료일 미정`
                          : `${formatDate(promo?.prmtStartDate || '', true)} ~ ${commonUtils.formatDate(promo.prmtEndDate || '')}`}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="promo-empty">
              <div>
                <img src={GGOOLLFF} alt="프로모션 이미지" width={'200px'} />
              </div>
              <div>진행중인 프로모션이 없어요.</div>
            </div>
          )}
        </>
      ) : (
        <div className="container">
          <section className="search-section">
            <form className="search-form">
              <div className="form-group">
                <label className="form-label">프로모션 종류&nbsp;&nbsp;</label>
                <div className="form-options">
                  {PromoType.map((type) => (
                    <label className="radio-option" key={type.value}>
                      <input
                        type="radio"
                        name="promoType"
                        value={type.value}
                        checked={checkboxValues.promoType === type.value}
                        onChange={() =>
                          handleCheckboxChange('promoType', type.value)
                        }
                      />
                      <span>{type.label}</span>
                      {type.value === '01' &&
                        checkboxValues.promoType === '01' && (
                          <select
                            id="areaCd"
                            name="areaCd"
                            style={{
                              marginLeft: '8px',
                              width: '80px',
                              height: '35px',
                            }}
                            onChange={(e) => setAreaCd(e.target.value)}
                          >
                            {areaList.map((option, index) => (
                              <option
                                key={`${option.areaCd}-${index}`}
                                value={option.areaCd}
                              >
                                {option.remark}
                              </option>
                            ))}
                          </select>
                        )}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">진행상태&nbsp;&nbsp;</label>
                <div className="form-options">
                  {StatusType.map((type) => (
                    <label className="radio-option" key={type.value}>
                      <input
                        type="radio"
                        name="statusType"
                        value={type.value}
                        checked={checkboxValues.statusType === type.value}
                        onChange={() =>
                          handleCheckboxChange('statusType', type.value)
                        }
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">검색조건&nbsp;&nbsp;</label>
                <div className="form-options">
                  {KeywordType.map((type) => (
                    <label className="radio-option" key={type.value}>
                      <input
                        type="checkbox"
                        name="keywordType"
                        value={type.value}
                        checked={checkboxValues.keywordTypes.includes(
                          type.value,
                        )}
                        onChange={() =>
                          handleCheckboxChange('keywordTypes', type.value)
                        }
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div
                className="promo-search-input-group"
                style={{
                  marginTop: '10px',
                  width: '100%',
                }}
              >
                <input
                  type="text"
                  className="promo-search-input"
                  placeholder="검색어 입력"
                  value={keyword}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyPress}
                />
                <button
                  type="button"
                  className="search-btn"
                  onClick={handleSearchClick}
                >
                  검색
                </button>
              </div>
            </form>
          </section>
          {promoList.length > 0 ? (
            <>
              <section className="promotion-grid">
                {promoList.map((promo) => {
                  const endDateUnlimited =
                    promo.prmtEndDate?.startsWith('2099-12-31')
                  const isExpired =
                    promo.prmtEndDate &&
                    new Date(promo.prmtEndDate) < new Date()
                  const now = new Date()
                  const startDate = promo.prmtStartDate
                    ? new Date(promo.prmtStartDate)
                    : null
                  const endDate = promo.prmtEndDate
                    ? new Date(promo.prmtEndDate)
                    : null

                  let status = '진행중'
                  if (startDate && now < startDate) status = '예정'
                  else if (endDate && now > endDate) status = '마감'

                  const badgeClass =
                    status === '예정' ? 'event' : status === '마감' ? 'end' : ''
                  return (
                    <div
                      className="promotion-card"
                      key={promo.nttSn}
                      onClick={() => {
                        navigate(`/promotion/detail?nttSn=${promo.nttSn}`)
                      }}
                      style={{
                        filter: isExpired ? 'grayscale(100%)' : 'none',
                      }}
                    >
                      <div className="card-image">
                        <img
                          className="img"
                          src={getThumbnailUrl(promo.thumbnailUrl)}
                          title={promo.nttSj}
                          alt="thumbnail"
                          onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = GGOOLLFF
                          }}
                        />
                      </div>
                      <div className="card-content">
                        <span className={`card-badge ${badgeClass}`}>
                          {status}
                        </span>
                        <h3 className="card-title">{promo.nttSj}</h3>
                        <p className="card-period">
                          기간 :&nbsp;
                          {endDateUnlimited
                            ? `${formatDate(promo?.prmtStartDate || '', true)} ~ 종료일 미정`
                            : `${formatDate(promo?.prmtStartDate || '', true)} ~ ${commonUtils.formatDate(promo.prmtEndDate || '')}`}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </section>
            </>
          ) : (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h3 className="no-results-title">검색 결과가 없습니다</h3>
              <p className="no-results-text">다른 키워드로 검색해보세요</p>
            </div>
          )}
        </div>
      )}
      <div className="pagination-wrapper">
        {pageCount > 0 && (
          <Pagination
            totalCount={pageCount}
            onPageChange={handlePageChange}
            paramPage={page}
            boardType={'promotion'}
          />
        )}
      </div>
    </>
  )
}
